import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pollingRegistry from '../utils/pollingRegistry';
import {
  faArrowLeft,
  faRefresh,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { isEnabledForPlatform, canonicalPlatform } from '../utils/accountSettingPlatformList';
import {
  optimizeEnabledFor,
  resolveOptimizePlatformCampaignId,
  startOptimizeRun,
  startAllFocusedAnalyses,
  ALL_ANALYSES_KIND,
} from '../utils/optimizeCampaignRun';
import { buildSuggestionEditRequest } from '../utils/optimizerSuggestionApply';
import { findSuggestionApplication } from '../utils/suggestionApplication';
import { isUploadCreativesHidden } from '../utils/uploadCreativesGating';
import { isResultSectionSharedWithAccount } from '../utils/resultSectionFlag';
import { getPlatformCampaignIds } from '../utils/platformCampaignIds';
import { buildYahooCampaignUrl } from '../utils/yahooDspUrl';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import * as browserNotifications from '../utils/browserNotifications';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useAppliedImpacts } from '../hooks/useAppliedImpacts';
import { hasChannelAccess, isAdmin, isSuperAdmin, canSendMessage } from '../utils/permissions';
// Removed Chat and TaskSidebar imports - components no longer used
import CampaignCompletionStatus from './CampaignCompletionStatus';
// Removed CampaignCompletionMessage import - not needed in campaign details page
import StatusBadge from './StatusBadge';
import GtLaunchedWorkspace from './GtLaunchedWorkspace/GtLaunchedWorkspace';
import GtLaunchHistory from './GtLaunchedWorkspace/GtLaunchHistory';
import { gtLaunchStateFromMessages } from './GtLaunchedWorkspace/gtLaunchState';
import CampaignActivity from './CampaignActivity';

import AssetProcessingNotification from './AssetProcessingNotification';
import UploadCreativesModal from './UploadCreativesModal';
import EditCampaignModal from './EditCampaignModal';
import OptimizeCampaignModal from './OptimizeCampaignModal';
import GeofenceAdGroupsModal from './GeofenceAdGroupsModal';
import AutoPacingScheduleList from './AutoPacingScheduleList';
import CreativeScheduleList from './CreativeScheduleList';
import AdGroupScheduleList from './AdGroupScheduleList';
import RetargetingWatchSection from './RetargetingWatchSection';
import FbBudgetAlertHistory from './FbBudgetAlertHistory';
import CampaignPlanningReview from './CampaignPlanningReview';
import './CampaignDetailPage.css';
import UpdateCreativeButton from './UpdateCreativeWorkflow/UpdateCreativeButton';

/**
 * CampaignDetailPage component for displaying campaign details and orchestration progress
 *
 * Features:
 * 1. Campaign information display
 * 2. Real-time orchestration progress tracking
 * 3. Step-by-step progress visualization
 * 4. Action buttons for campaign management
 */
const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();


  // State management
  const [campaign, setCampaign] = useState(null);
  const [orchestrationStatus, setOrchestrationStatus] = useState(null);
  const [orchestrationStatusCampaignId, setOrchestrationStatusCampaignId] = useState(null);
  const [isOrchestrationStarted, setIsOrchestrationStarted] = useState(false);
  const [assigningMessageId, setAssigningMessageId] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [messagePollingStates, setMessagePollingStates] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [waitingForContext, setWaitingForContext] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCreativesData, setUploadCreativesData] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [applyingChanges, setApplyingChanges] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditingCampaign, setIsEditingCampaign] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  // Which focused analysis the pressed button asked for; null = combined optimizer.
  const [optimizingAnalysisKind, setOptimizingAnalysisKind] = useState(null);
  const [optimizingCampaignId, setOptimizingCampaignId] = useState(null);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [geofenceCampaignId, setGeofenceCampaignId] = useState(null);
  const [isCreatingGeofence, setIsCreatingGeofence] = useState(false);

  // Fetch account settings to check for optimize-campaign feature
  const { settings: accountSettings } = useAccountSettings(accountId, user);

  // V3 UI ("Open in new UI") gate — mirrors CampaignsPage.js:33-34 so a
  // single account flag + role check controls visibility everywhere.
  //   - Super admins always see it (bypasses the account flag).
  //   - Account admins see it only when `enable-v3-ui` is 'true'.
  //   - All other roles never see it.
  const v3UiEnabled = isSuperAdmin(user)
    || (isAdmin(user) && accountSettings?.['enable-v3-ui'] === 'true');
  // Opt-in planning access for every role; does not enable other V3 surfaces.
  const planningGridForAllRoles = accountSettings?.['enable-v3-ui-planning-all-roles'] === 'true';

  // Super-admin powers that must survive impersonation.
  //
  // isSuperAdmin(user) alone goes FALSE the moment impersonation starts: the
  // impersonate route returns the TARGET user's `permissions`, and nothing —
  // not the route, not handleImpersonateUser — ever sets `user.isSuperAdmin`
  // (the "impersonated users carry it" note in permissions.js is wrong).
  //
  // Reading isImpersonating is sound: /api/admin/impersonate is behind the
  // isSuperAdmin middleware and refuses to target another Super Admin, so an
  // impersonating session is always being driven by a super admin.
  const actingAsSuperAdmin = isSuperAdmin(user) || !!user?.isImpersonating;

  // Campaign Processor / Launcher result-zip download buttons: super admins
  // (impersonating or not) always; account admins only when
  // `enable-campaign-result-zip-download` is 'true'.
  const campaignResultZipEnabled = actingAsSuperAdmin
    || (isAdmin(user) && accountSettings?.['enable-campaign-result-zip-download'] === 'true');

  // The activity item's raw response JSON and its Download JSON button.
  // Super admins always. Otherwise opt-in per account via
  // `enable-result-section`, which opens it to EVERY role on the account —
  // not just admins — so the whole team can read what a run returned.
  // Absent or any value other than the string 'true' keeps it hidden, which
  // is the default for every account.
  const canViewRawJson = actingAsSuperAdmin
    || isResultSectionSharedWithAccount(accountSettings);

  // Account-level `hide-upload-creatives` setting removes every upload
  // entry point on this page. Children (CampaignCompletionStatus,
  // CampaignActivity) render no upload buttons when the handler is null.
  const uploadCreativesHidden = isUploadCreativesHidden(accountSettings);

  // Ask the browser for notification permission when the reviewer lands on a
  // campaign detail page AND the account has opted in via the
  // `enable-chrome-notifications` setting (same opt-in shape as
  // `email-notifications`). The helper short-circuits if permission is
  // already granted or denied, so this is safe to run on every settings
  // change. Chromes may silently return 'default' when called outside a
  // user gesture; the subsequent notify() call is then a no-op and we
  // quietly fall back to email-only.
  useEffect(() => {
    if (accountSettings?.['enable-chrome-notifications'] === 'true') {
      browserNotifications.requestPermission();
    }
  }, [accountSettings]);


  // Ref to store polling interval for cleanup
  const pollIntervalRef = useRef(null);

  // Ref to track last successful poll time for health check
  const lastPollTimeRef = useRef(null);

  // Debug: Log when campaignMessages changes
  useEffect(() => {
    console.log('🔄 campaignMessages state updated:', campaignMessages.map(m => ({
      id: m.message_id,
      action: m.action,
      status: m.status,
      hasJsonResponse: !!m.json_response
    })));
  }, [campaignMessages]);

  // Cleanup polling function
  const cleanupPolling = useCallback(() => {
    const pollingId = `campaign-${campaignId}-orchestration`;

    // Use polling registry to cleanup orchestration polling
    pollingRegistry.cleanup(pollingId);

    // Also clear local reference
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);

    // NOTE: We do NOT cleanup all message polling here because individual messages
    // (like assign-creatives, upload-creatives, etc.) should manage their own polling
    // lifecycle independently of the orchestration polling. They will stop when their
    // own status changes to completed/failed/etc.
  }, [campaignId]);

  // Old campaign-list links and bookmarks land here. Opted-in accounts use
  // the grid by default once their own orchestration status confirms the mode.
  // Replace history so Back does not reopen this route and immediately bounce.
  useEffect(() => {
    if (!planningGridForAllRoles || !campaignId || campaign?.id !== campaignId
      || orchestrationStatusCampaignId !== campaignId
      || orchestrationStatus?.planningMode !== true
      || orchestrationStatus?.status === 'completed') return;
    cleanupPolling();
    navigate(`/campaign/v3/start?campaign=${encodeURIComponent(campaignId)}`, { replace: true });
  }, [planningGridForAllRoles, campaignId, campaign?.id, orchestrationStatusCampaignId,
    orchestrationStatus?.planningMode, orchestrationStatus?.status, cleanupPolling, navigate]);

  // Cleanup all message polling
  const cleanupAllMessagePolling = () => {
    messagePollingStates.forEach((pollingState, messageId) => {
      stopMessagePolling(messageId);
    });
  };

  // Synchronize message polling states with current message statuses
  const synchronizeMessagePolling = (messages) => {
    // Stop polling for messages that are no longer processing
    messagePollingStates.forEach((pollingState, messageId) => {
      const message = messages.find(m => m.message_id === messageId);
      if (!message || message.status !== 'processing') {
        console.log(`🛑 Stopping polling for message ${messageId} - status changed to ${message?.status || 'not found'}`);
        stopMessagePolling(messageId);
      }
    });

    // Debug: Log all messages and their statuses
    console.log('🔍 Checking messages for polling:', messages.map(m => ({
      id: m.message_id,
      type: m.message_type,
      action: m.action,
      status: m.status
    })));

    console.log('🔍 Currently polling messages:', Array.from(messagePollingStates.keys()));

    // Start polling for new processing messages
    messages.forEach(message => {
      if (message.status === 'processing' && !messagePollingStates.has(message.message_id)) {
        // Skip polling for ALL orchestration messages - they should use orchestration polling
        const isOrchestrationMessage = ['orchestration', 'fast-track', 'fast-track-shell', 'salesforce-orchestration'].includes(message.message_type) ||
                                      ['fast-track', 'fast-track-shell', 'salesforce-orchestration'].includes(message.action);

        console.log(`🔍 Message ${message.message_id} check:`, {
          status: message.status,
          message_type: message.message_type,
          action: message.action,
          isOrchestrationMessage,
          alreadyPolling: messagePollingStates.has(message.message_id)
        });

        if (isOrchestrationMessage) {
          console.log(`⏭️ Skipping message polling for orchestration message ${message.message_id} (${message.message_type || message.action}) - use orchestration polling instead`);
          return;
        }

        // Only poll single messages (upload-creatives, assign-creatives, etc.)
        console.log(`🔄 Starting polling for newly processing message ${message.message_id}`);
        startMessagePolling(message.message_id);
      } else if (message.status === 'processing' && messagePollingStates.has(message.message_id)) {
        console.log(`✅ Message ${message.message_id} is already being polled`);
      }
    });
  };

  // Retry mechanism for context loading
  const retryContextLoad = () => {
    setWaitingForContext(true);
    setError(null);
    setIsLoading(true);
  };

  // Fetch campaign details
  const fetchCampaignDetails = async () => {
    if (!campaignId) {
      setError('No campaign ID provided');
      setIsLoading(false);
      return;
    }

    // Try to fetch even with partial context - backend might handle it
    const params = {};
    if (accountId) params.accountId = accountId;
    if (user) params.userId = user?.userId || user?.id;

    try {
      console.log(`🔄 Fetching campaign details for ${campaignId}`, { accountId, userId: user?.userId || user?.id });

      const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
        params
      });

      console.log(`✅ Successfully loaded campaign: ${response.data.name}`, {
        orchestration_chat_id: response.data.orchestration_chat_id,
        orchestration_message_id: response.data.orchestration_message_id
      });
      setCampaign(response.data);

      // If campaign has orchestration, start polling for status (only if not already complete/polling)
      if (response.data.orchestration_chat_id && response.data.orchestration_message_id) {
        const isComplete = orchestrationStatus && ['completed', 'failed', 'stopped', 'timeout', 'review'].includes(orchestrationStatus.status);
        if (!isComplete && !isPolling) {
          startOrchestrationPolling(response.data.orchestration_chat_id, response.data.orchestration_message_id);
        }
      }

      // Fetch associated messages (only if we have full context)
      if (accountId && user) {
        await fetchCampaignMessages();
      } else {
        console.log('⏳ Skipping message fetch - waiting for full context');
      }
    } catch (err) {
      console.error('Error fetching campaign details:', err);
      if (err.response?.status === 404) {
        setError('Campaign not found');
      } else if (err.response?.status === 403) {
        setError('Access denied to this campaign');
      } else if (err.response?.status === 401) {
        setError('Please log in to view this campaign');
      } else {
        setError('Failed to load campaign details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch campaign messages
  const fetchCampaignMessages = async (options = {}) => {
    const { skipAutoPolling = false } = options;

    if (!campaignId || !accountId || !user) return;

    try {
      const params = {
        accountId,
        userId: user?.userId || user?.id
      };

      const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/messages`), {
        params
      });

      const messages = response.data.messages || [];
      console.log('📨 Fetched campaign messages:', messages.map(m => ({
        id: m.message_id,
        action: m.action,
        status: m.status,
        hasJsonResponse: !!m.json_response
      })));
      setCampaignMessages(messages);

      // Synchronize message polling states with current message statuses (unless skipAutoPolling is true)
      if (!skipAutoPolling) {
        synchronizeMessagePolling(messages);
      } else {
        console.log('⏭️ Skipping auto-polling synchronization (will start polling manually)');
      }
    } catch (err) {
      console.error('Error fetching campaign messages:', err);
      // Don't set error state for messages fetch failure, just log it
    }
  };

  // Start polling for a single message status
  const startMessagePolling = async (messageId) => {
    if (messagePollingStates.has(messageId)) {
      return; // Already polling this message
    }

    console.log(`🔄 Starting polling for message ${messageId}`);

    const pollMessage = async () => {
      try {
        const params = {
          accountId,
          userId: user?.userId || user?.id,
          _t: Date.now() // Cache buster to prevent stale responses
        };

        const response = await api.get(getApiUrl(`/api/v2/message/${messageId}/status`), {
          params,
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        const messageStatus = response.data;

        console.log(`📥 Received message status for ${messageId}:`, {
          status: messageStatus.status,
          action: messageStatus.action,
          hasResult: !!messageStatus.result,
          resultKeys: messageStatus.result ? Object.keys(messageStatus.result) : [],
          updatedAt: messageStatus.updatedAt
        });

        // Update the specific message in the campaign messages list
        setCampaignMessages(prevMessages => {
          const messageFound = prevMessages.find(msg => msg.message_id === messageId);
          if (!messageFound) {
            console.warn(`⚠️ Message ${messageId} not found in campaignMessages array!`);
            return prevMessages;
          }

          const updatedMessages = prevMessages.map(msg => {
            if (msg.message_id === messageId) {
              const statusChanged = msg.status !== messageStatus.status;
              const resultChanged = JSON.stringify(msg.json_response) !== JSON.stringify(messageStatus.result);

              console.log(`🔄 Updating message ${messageId}:`, {
                oldStatus: msg.status,
                newStatus: messageStatus.status,
                statusChanged,
                oldHasJsonResponse: !!msg.json_response,
                newHasJsonResponse: !!messageStatus.result,
                resultChanged,
                action: msg.action,
                messageType: msg.message_type
              });

              if (statusChanged || resultChanged) {
                console.log(`✨ Message ${messageId} state changed - triggering re-render`);
              }

              return { ...msg, status: messageStatus.status, json_response: messageStatus.result };
            }
            return msg;
          });

          console.log(`📋 Updated campaign messages:`, updatedMessages.map(m => ({
            id: m.message_id,
            action: m.action,
            messageType: m.message_type,
            status: m.status,
            hasJsonResponse: !!m.json_response
          })));

          return updatedMessages;
        });

        // Stop polling if message is complete
        if (['completed', 'failed', 'error', 'timeout'].includes(messageStatus.status)) {
          console.log(`✅ Message ${messageId} completed with status: ${messageStatus.status}`);

          // Refresh campaign details and messages when assign-creatives completes
          if (messageStatus.action === 'assign-creatives' && messageStatus.status === 'completed') {
            console.log(`🔄 Refreshing campaign details and messages after assign-creatives completion`);
            setTimeout(async () => {
              await fetchCampaignDetails();
              await fetchCampaignMessages(); // Refresh messages to get ad_sdf_gcs_path
            }, 500); // Small delay to ensure backend has updated campaign status
          }

          // Refresh campaign details when import-campaign completes or fails (status may change)
          if (messageStatus.action === 'import-campaign') {
            console.log(`🔄 Refreshing campaign details after import-campaign ${messageStatus.status}`);
            setTimeout(async () => {
              await fetchCampaignDetails();
            }, 500);
          }

          stopMessagePolling(messageId);
          return false; // Stop polling
        }

        return true; // Continue polling
      } catch (error) {
        console.error(`Error polling message ${messageId}:`, error);
        return true; // Continue polling despite error
      }
    };

    // Initial poll
    const shouldContinue = await pollMessage();

    if (shouldContinue) {
      // Set up interval polling
      const intervalId = setInterval(async () => {
        const shouldContinue = await pollMessage();
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      }, 5000); // Poll every 5 seconds

      // Store polling state
      setMessagePollingStates(prev => new Map(prev.set(messageId, {
        intervalId,
        startTime: Date.now()
      })));

      // Register with polling registry
      const pollingId = `message-${messageId}`;
      pollingRegistry.register(
        pollingId,
        intervalId,
        'CampaignDetailPage',
        `Message polling for ${messageId}`
      );
    }
  };

  // Stop polling for a single message
  const stopMessagePolling = (messageId) => {
    const pollingState = messagePollingStates.get(messageId);
    if (pollingState) {
      clearInterval(pollingState.intervalId);

      // Remove from polling states
      setMessagePollingStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(messageId);
        return newMap;
      });

      // Cleanup from polling registry
      const pollingId = `message-${messageId}`;
      pollingRegistry.cleanup(pollingId);

      console.log(`🛑 Stopped polling for message ${messageId}`);
    }
  };

  // Start polling for orchestration status
  const startOrchestrationPolling = async (chatId, messageId) => {
    console.log(`🔄 startOrchestrationPolling called`, {
      chatId,
      messageId,
      isPolling,
      hasAccountId: !!accountId,
      hasUser: !!user
    });

    if (isPolling) {
      console.log('⏸️ Already polling, returning early');
      return;
    }

    if (!accountId || !user) {
      console.log('⚠️ Missing accountId or user, cannot start polling');
      return;
    }

    // Clear any existing polling interval
    if (pollIntervalRef.current) {
      console.log('🧹 Clearing existing polling interval');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Stop any existing message polling for this orchestration message to avoid duplicates
    if (messagePollingStates.has(messageId)) {
      console.log(`🛑 Stopping existing message polling for orchestration ${messageId} to avoid duplicates`);
      stopMessagePolling(messageId);
    }

    console.log(`✅ Starting orchestration polling for message ${messageId}`);
    setIsPolling(true);

    // Add error counter for logging (but don't stop polling)
    let consecutiveErrors = 0;

    const pollStatus = async () => {
      try {
        // Use v2 orchestration status endpoint for better status calculation
        const response = await api.get(getApiUrl(`/api/v2/orchestration/${messageId}/status`), {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        });

        // Reset error counter on successful request
        consecutiveErrors = 0;
        lastPollTimeRef.current = Date.now();
        setOrchestrationStatus(response.data);
        setOrchestrationStatusCampaignId(campaignId);

        // Update orchestration message status in campaign messages
        // Only update the primary orchestration message, not other messages that might have the same ID
        setCampaignMessages(prevMessages =>
          prevMessages.map(msg => {
            const isOrchestrationMessage = ['orchestration', 'fast-track', 'fast-track-shell', 'salesforce-orchestration'].includes(msg.message_type) ||
                                          ['fast-track', 'fast-track-shell', 'salesforce-orchestration'].includes(msg.action);

            // Update ONLY the primary orchestration message with matching ID
            if (isOrchestrationMessage && msg.is_primary && msg.message_id === messageId) {
              console.log(`🔄 Updating primary orchestration message ${messageId} status to ${response.data.status}`);
              return { ...msg, status: response.data.status };
            }
            return msg;
          })
        );

        // Stop polling if orchestration is complete, failed, stopped, or timed out
        if (['completed', 'failed', 'stopped', 'timeout'].includes(response.data.status)) {
          // Check if API failed even when orchestration shows as "completed"
          const apiHasFailed = response.data.stepProgress?.api?.status === 'failed' ||
                              (response.data.apiResult?.success === false);

          // Fire a Chrome notification mirroring the server-side email send.
          // Gated on the `enable-chrome-notifications` account setting (same
          // explicit opt-in shape as `email-notifications`). Tagged by
          // campaignId so a duplicate polling tick (or a reviewer bouncing
          // between tabs) replaces rather than stacks. Clicking focuses this
          // tab and navigates here. Entirely silent no-op when permission
          // isn't granted, account-setting is off, or helper-level toggle is
          // off — all handled inside notify() + the guard below.
          const chromeNotificationsEnabled =
            accountSettings?.['enable-chrome-notifications'] === 'true';
          const statusForNotification = apiHasFailed ? 'failed' : response.data.status;
          const campaignName = campaign?.name || `Campaign ${campaignId}`;
          const notifTitle =
            statusForNotification === 'completed' ? `Campaign launched: ${campaignName}` :
            statusForNotification === 'failed'    ? `Campaign failed: ${campaignName}` :
            statusForNotification === 'timeout'   ? `Campaign timed out: ${campaignName}` :
            statusForNotification === 'stopped'   ? `Campaign stopped: ${campaignName}` :
            `Campaign update: ${campaignName}`;
          const notifBody =
            statusForNotification === 'completed' ? 'Your campaign finished processing successfully.' :
            statusForNotification === 'failed'    ? 'Processing encountered an error — open the campaign to see details.' :
            statusForNotification === 'timeout'   ? 'The orchestration timed out. Open the campaign for next steps.' :
            statusForNotification === 'stopped'   ? 'The campaign was stopped before completion.' :
            '';
          if (chromeNotificationsEnabled) {
            browserNotifications.notify({
              title: notifTitle,
              body: notifBody,
              tag: `campaign-${campaignId}`,
              onClick: () => {
                // If the user is on another tab/page, bring them back here.
                if (window.location.pathname !== `/campaigns/${campaignId}`) {
                  navigate(`/campaigns/${campaignId}`);
                }
              },
            });
          }

          // Refresh campaign data to get updated campaign status for completion, failure, or API failure
          if (['completed', 'failed', 'timeout'].includes(response.data.status) || apiHasFailed) {
            try {
              const campaignResponse = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
                params: {
                  accountId,
                  userId: user?.userId || user?.id
                }
              });
              // Update campaign data and explicitly set status if API failed
              const updatedCampaign = campaignResponse.data;
              if (apiHasFailed && updatedCampaign.status !== 'failed') {
                console.log(`🔄 Setting campaign status to 'failed' due to API failure`);

                // Update status in database using PUT endpoint (PATCH doesn't support status updates)
                try {
                  await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
                    name: updatedCampaign.name,
                    description: updatedCampaign.description,
                    status: 'failed', // Use 'failed' status for failed campaigns
                    advertiserId: updatedCampaign.advertiser_id
                  }, {
                    params: {
                      accountId,
                      userId: user?.userId || user?.id
                    }
                  });

                  // Update local state with the new status
                  updatedCampaign.status = 'failed';
                  console.log(`✅ Successfully updated campaign status to 'failed' in database`);
                } catch (statusUpdateError) {
                  console.error('Error updating campaign status to failed:', statusUpdateError);
                  // Still update local state even if database update fails
                  updatedCampaign.status = 'failed';
                }
              }

              setCampaign(updatedCampaign);

              const statusDescription = apiHasFailed ? `${response.data.status} (API failed)` : response.data.status;
              console.log(`🔄 Refreshed campaign data after orchestration ${statusDescription}`);
            } catch (refreshError) {
              console.error(`Error refreshing campaign data after orchestration ${response.data.status}:`, refreshError);
              // Don't fail the polling cleanup, just log the error
            }
          }

          // Refresh campaign messages to reflect latest state after orchestration completion
          try {
            await fetchCampaignMessages();
            console.log(`🔄 Refreshed campaign messages after orchestration ${response.data.status}`);
          } catch (refreshError) {
            console.error('Error refreshing campaign messages after orchestration completion:', refreshError);
            // Don't fail the polling cleanup, just log the error
          }

          cleanupPolling();
          return false; // Stop polling
        }



        return true; // Continue polling
      } catch (err) {
        consecutiveErrors++;
        console.error(`Error polling orchestration status (attempt ${consecutiveErrors}):`, err);

        // Always continue polling despite errors - never give up!
        return true;
      }
    };

    // Initial poll
    const shouldContinue = await pollStatus();

    if (shouldContinue) {
      // Set up interval polling and store reference
      pollIntervalRef.current = setInterval(async () => {
        const shouldContinue = await pollStatus();
        if (!shouldContinue) {
          cleanupPolling();
        }
      }, 3000); // Poll every 3 seconds

      // Register with polling registry
      const pollingId = `campaign-${campaignId}-orchestration`;
      pollingRegistry.register(
        pollingId,
        pollIntervalRef.current,
        'CampaignDetailPage',
        `Orchestration polling for campaign ${campaignId} (chatId: ${chatId}, messageId: ${messageId})`
      );
    }
  };

  // Handle stopping single message
  const handleStopMessage = async (messageId) => {
    if (!messageId) {
      console.error('Cannot stop message: missing message ID');
      return;
    }

    try {
      console.log(`🛑 Stopping single message ${messageId}`);

      await api.post(
        getApiUrl(`/api/v2/message/${messageId}/stop`),
        {},
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        }
      );

      console.log(`✅ Successfully stopped message ${messageId}`);

      // Update the message status in local state
      setCampaignMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.message_id === messageId) {
            console.log(`🔄 Updating message ${messageId} status to 'stopped'`);
            return { ...msg, status: 'stopped' };
          }
          return msg;
        });
      });

      // Stop polling for this message
      stopMessagePolling(messageId);

      // Refresh campaign messages to reflect latest state
      try {
        await fetchCampaignMessages();
        console.log('🔄 Refreshed campaign messages after message stop');
      } catch (refreshError) {
        console.error('Error refreshing campaign messages after message stop:', refreshError);
      }

    } catch (error) {
      console.error('Error stopping message:', error);
      alert(`Failed to stop message: ${error.response?.data?.error || error.message}`);
    }
  };

  // Handle stopping orchestration
  const handleStopOrchestration = async () => {
    if (!orchestrationStatus || !campaign?.orchestration_message_id) {
      console.error('Cannot stop orchestration: missing orchestration data');
      return;
    }

    try {
      await api.post(
        getApiUrl(`/api/v2/orchestration/${campaign.orchestration_message_id}/stop`),
        {},
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        }
      );

      // Update orchestration status immediately to show stopped state
      setOrchestrationStatus(prev => {
        if (!prev) return prev;

        // Update overall status and individual step statuses
        const updatedStepProgress = { ...prev.stepProgress };

        // Update any currently processing steps to stopped
        if (updatedStepProgress.campaign?.status === 'processing') {
          updatedStepProgress.campaign = { ...updatedStepProgress.campaign, status: 'stopped' };
        }
        if (updatedStepProgress.launcher?.status === 'processing') {
          updatedStepProgress.launcher = { ...updatedStepProgress.launcher, status: 'stopped' };
        }
        if (updatedStepProgress.creative?.status === 'processing') {
          updatedStepProgress.creative = { ...updatedStepProgress.creative, status: 'stopped' };
        }
        if (updatedStepProgress.api?.status === 'processing') {
          updatedStepProgress.api = { ...updatedStepProgress.api, status: 'stopped' };
        }

        return {
          ...prev,
          status: 'stopped',
          stepProgress: updatedStepProgress
        };
      });

      // Update campaign status to 'stopped' in database
      try {
        console.log(`🔄 Setting campaign status to 'stopped' due to orchestration stop`);

        await api.put(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
          name: campaign.name,
          description: campaign.description,
          status: 'stopped',
          advertiserId: campaign.advertiser_id
        }, {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        });

        // Update local campaign state with the new status
        setCampaign(prev => ({
          ...prev,
          status: 'stopped'
        }));

        console.log(`✅ Successfully updated campaign status to 'stopped' in database`);
      } catch (statusUpdateError) {
        console.error('Error updating campaign status to stopped:', statusUpdateError);
        // Still update local state even if database update fails
        setCampaign(prev => ({
          ...prev,
          status: 'stopped'
        }));
      }

      // Refresh campaign messages to reflect latest state after orchestration stop
      try {
        await fetchCampaignMessages();
        console.log('🔄 Refreshed campaign messages after orchestration stop');
      } catch (refreshError) {
        console.error('Error refreshing campaign messages after orchestration stop:', refreshError);
        // Don't fail the cleanup, just log the error
      }

      // Stop polling since orchestration is stopped
      cleanupPolling();

    } catch (error) {
      console.error('Error stopping orchestration:', error);
      // Could add error toast notification here
    }
  };

  // Removed handleViewChat - chat interface no longer used in campaign details

  // Handle upload creatives
  const handleUploadCreatives = () => {
    setShowUploadModal(true);
  };

  const handleUploadComplete = (uploadData) => {
    console.log('🎯 Upload completed, data:', uploadData);
    setUploadCreativesData(uploadData);

    // Single messages are now completely separate from orchestration
    // Refresh campaign messages first, then start polling
    console.log('🎯 Refreshing campaign messages before starting polling for:', uploadData.uploadId);

    // Refresh campaign data to pick up orchestration_chat_id saved by upload-creatives
    setTimeout(async () => {
      console.log('🔄 Refreshing campaign data after upload-creatives');
      await fetchCampaignDetails();
      // Skip auto-polling since we'll manually start polling below
      await fetchCampaignMessages({ skipAutoPolling: true });

      // Now start message polling after messages are loaded
      if (uploadData.uploadId) {
        console.log('🎯 Starting message polling for single message:', uploadData.uploadId, 'Type:', uploadData.messageType);
        startMessagePolling(uploadData.uploadId);
      }
    }, 1000); // Small delay to ensure backend has saved the chat_id
  };

  // Handle optimize campaign
  // campaignIdParam: optional specific campaign ID to optimize (for multi-campaign scenarios)
  // analysisKind: null runs the combined optimizer ("Optimize Campaign"); 'diagnostic',
  // 'pacing' or 'performance' run one focused analysis. It is remembered here rather than
  // passed through the modal's own state so the modal stays unaware of it — the modal
  // collects a window and a focus, which every analysis takes.
  const handleOptimizeCampaign = async (campaignIdParam = null, analysisKind = null) => {
    if (!campaign) {
      console.error('No campaign found');
      alert('Unable to optimize campaign: Campaign not found');
      return;
    }

    // If a specific campaign ID is provided, use it directly. Otherwise derive it
    // through utils/optimizeCampaignRun, which is now the one implementation —
    // shared with the campaign grid panel and the sidekick Optimization agent,
    // including the advertiser_id fallback and its LinkedIn/PMax/DemandGen
    // exception.
    const platformCampaignId = campaignIdParam || resolveOptimizePlatformCampaignId(campaign);

    if (!platformCampaignId) {
      alert('Unable to optimize campaign: Platform campaign ID not found. Please ensure the campaign has been launched successfully.');
      return;
    }

    // The modal now opens for EVERY platform, not just MadHive. It used to be
    // MadHive-only because MadHive was the only one with something to collect (a
    // reporting-metrics ZIP); the others went straight to a run. Now every run
    // has a reporting window and an optional focus to choose, and this modal is
    // where that choice is made — so a direct dispatch would be the one path that
    // silently takes the defaults and never offers them.
    setOptimizingCampaignId(platformCampaignId);
    setOptimizingAnalysisKind(analysisKind);
    setShowOptimizeModal(true);
  };

  // Perform the actual optimization (called from modal or directly)
  const performOptimize = async (platformCampaignId, reportingMetricsPath = null, runFields = {}) => {
    setIsOptimizing(true);

    try {
      console.log('🎯 Optimizing campaign:', {
        campaignId: campaignId,
        platformCampaignId: platformCampaignId,
        destination: campaign.destination,
        reportingMetricsPath: reportingMetricsPath,
        analysis: optimizingAnalysisKind || 'combined'
      });

      // Shared dispatch — see utils/optimizeCampaignRun.
      const runParams = {
        campaignId,
        platformCampaignId,
        reportingMetricsPath,
        // Reporting window + focus, chosen in OptimizeCampaignModal.
        customPrompt: runFields.customPrompt || null,
        dateParams: runFields.dateParams || null,
        accountId,
        userId: user?.userId || user?.id,
      };
      if (optimizingAnalysisKind === ALL_ANALYSES_KIND) {
        // "Run Full Analysis": all three focused analyses as separate runs,
        // each with its own message and result to poll.
        const { messageIds } = await startAllFocusedAnalyses(runParams);
        console.log('✅ Focused analysis messages created:', messageIds);
        messageIds.filter(Boolean).forEach((id) => startMessagePolling(id));
      } else {
        const { messageId } = await startOptimizeRun({
          ...runParams,
          analysisKind: optimizingAnalysisKind,
        });
        console.log('✅ Optimize campaign message created:', messageId);

        // Start polling for the new message
        if (messageId) {
          startMessagePolling(messageId);
        }
      }

      // Refresh campaign messages to show the new message
      await fetchCampaignMessages();
    } catch (error) {
      console.error('❌ Error optimizing campaign:', error);
      alert(`Failed to optimize campaign: ${error.message}`);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Handle optimize complete from modal
  const handleOptimizeComplete = async (data) => {
    if (data.platformCampaignId) {
      await performOptimize(data.platformCampaignId, data.reportingMetricsPath || null, data);
    }
  };

  // Build the list of TTD platform campaigns (id + name) available for this
  // campaign record, so the geofence modal can let the user pick one. Reuses
  // getPlatformUrls() — the same extractor the action buttons use — which
  // resolves names from the import/orchestration result (campaign_id_to_name,
  // sandbox_urls, etc.). All options share this campaign record, so the
  // create-geofence-adgroups POST (keyed by the internal campaign id) just
  // varies the platformCampaignId.
  const getTtdCampaignOptions = () => {
    if (campaign?.destination?.toLowerCase() !== 'ttd') return [];
    const opts = [];
    const seen = new Set();
    const add = (id, name) => {
      const key = id ? String(id) : '';
      if (key && !seen.has(key)) {
        seen.add(key);
        opts.push({ id: key, name: name || null });
      }
    };
    (getPlatformUrls() || []).forEach((u) => {
      if (Array.isArray(u.campaigns)) {
        u.campaigns.forEach((c) => add(c.campaign_id, c.name));
      } else {
        add(u.campaign_id, campaign?.name);
      }
    });
    // Always include the campaign's own platform id as a fallback option.
    add(campaign?.platform_campaign_id, campaign?.name);
    return opts;
  };

  // ---- Create Geofence Ad Groups (TTD) -------------------------------------
  // Open the upload modal. campaignIdParam is the TTD platform campaign id.
  const handleCreateGeofenceAdGroups = (campaignIdParam = null) => {
    if (!campaign) {
      alert('Unable to create geofence ad groups: Campaign not found');
      return;
    }
    const platformCampaignId = campaignIdParam || campaign.platform_campaign_id;
    if (!platformCampaignId) {
      alert('Unable to create geofence ad groups: TTD campaign ID not found. Ensure the campaign has been launched.');
      return;
    }
    setGeofenceCampaignId(platformCampaignId);
    setShowGeofenceModal(true);
  };

  // Called from the modal once the Excel is uploaded — dispatch the job.
  // Progress is tracked via the shared message poller (startMessagePolling),
  // which polls /api/v2/message/:id/status; that endpoint reads the pipeline's
  // GCS result and flips the activity-feed message to completed/failed.
  const performCreateGeofence = async ({ platformCampaignId, excelGcsPath, radius, dryRun }) => {
    setIsCreatingGeofence(true);
    try {
      const response = await api.post(`/api/v2/campaigns/${campaignId}/create-geofence-adgroups`, {
        platformCampaignId,
        excelGcsPath,
        radius,
        dryRun,
        accountId,
        userId: user?.userId || user?.id,
      });

      if (response.data.success) {
        if (response.data.messageId) {
          startMessagePolling(response.data.messageId);
        }
        await fetchCampaignMessages();
        alert('Geofence ad group creation started. This runs in the background (~10–40 min); progress shows in Campaign Activity.');
      } else {
        throw new Error(response.data.error || 'Failed to start geofence ad group creation');
      }
    } catch (error) {
      console.error('❌ Error starting geofence ad group creation:', error);
      alert(`Failed to start geofence ad group creation: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsCreatingGeofence(false);
    }
  };

  // Handle refresh campaign - fetch latest campaign data from platform
  /**
   * Run a campaign quality audit on demand.
   *
   * The automatic audit (account setting 'enable-campaign-audit') fires seconds
   * after launch. This is for afterwards — you act on a finding, then re-check.
   * It is read-only against the DSP, so it is safe to run as often as wanted.
   *
   * The findings arrive as a new message in the Activity feed, the same way an
   * optimize run's do, so there is nothing to render here beyond starting it.
   */
  const handleRunAudit = async () => {
    if (isAuditing) return;
    setIsAuditing(true);
    try {
      const response = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/audit`), {
        accountId,
        userId: user?.userId || user?.id,
      });
      if (response.data?.messageId) {
        startMessagePolling(response.data.messageId);
      }
      await fetchCampaignMessages();
    } catch (error) {
      // The route explains refusals precisely (not launched, no launch template,
      // unsupported platform). Relay that rather than a generic failure, because
      // each one tells the user something different about their campaign.
      const message = error?.response?.data?.message
        || error?.response?.data?.error
        || error.message;
      alert(`Could not start the campaign audit: ${message}`);
    } finally {
      setIsAuditing(false);
    }
  };

  const handleRefreshCampaign = async (campaignIdParam = null) => {
    if (!campaign) {
      console.error('No campaign found');
      alert('Unable to refresh campaign: Campaign not found');
      return;
    }

    // If a specific campaign ID is provided, use it directly
    let platformCampaignId = campaignIdParam;

    // Otherwise, extract platform campaign ID from campaign record
    if (!platformCampaignId) {
      platformCampaignId = campaign.platform_campaign_id || campaign.advertiser_id;
    }

    if (!platformCampaignId) {
      alert('Unable to refresh campaign: Platform campaign ID not found.');
      return;
    }

    setIsRefreshing(true);

    try {
      console.log('🔄 Refreshing campaign:', {
        campaignId: campaignId,
        platformCampaignId: platformCampaignId,
        destination: campaign.destination
      });

      // Call v2 API endpoint to create refresh message
      const response = await api.post(`/api/v2/campaigns/${campaignId}/refresh`, {
        platformCampaignId: platformCampaignId,
        accountId: accountId,
        userId: user?.userId || user?.id
      });

      if (response.data.success) {
        console.log('✅ Refresh campaign message created:', response.data.messageId);

        // Start polling for the new message
        if (response.data.messageId) {
          startMessagePolling(response.data.messageId);
        }

        // Refresh campaign messages to show the new message in the activity feed
        // Don't navigate away - stay on the same page
        await fetchCampaignMessages();
      } else {
        throw new Error(response.data.error || 'Failed to create refresh message');
      }
    } catch (error) {
      console.error('❌ Error refreshing campaign:', error);
      alert(`Failed to refresh campaign: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // The measured effect of what has already been applied, for the
  // recommendations table's Actual Impact column. Keyed by the same
  // (optimize message, suggestion index) pair isSuggestionApplied uses.
  // Refetched when the number of completed edits changes, so the column
  // appears after an apply without polling for a measurement that cannot
  // exist for days.
  const appliedEditCount = (campaignMessages || []).filter(
    (m) => String(m?.action || m?.message_type || '') === 'edit-campaign',
  ).length;
  const { byRun: appliedImpacts } = useAppliedImpacts(
    campaignId, accountId, appliedEditCount,
  );

  // Helper function to check if a suggestion has already been applied
  const isSuggestionApplied = (optimizeMessageId, suggestionIndex) => {
    return findSuggestionApplication(campaignMessages, optimizeMessageId, suggestionIndex, {
      destination: campaign?.destination,
      platformCampaignId: campaign?.platform_campaign_id,
    });
  };

  // Handle apply changes from optimization suggestions
  const handleApplyChanges = async (message, suggestion, suggestionIndex) => {
    if (!campaign) {
      console.error('No campaign found');
      alert('Unable to apply changes: Campaign not found');
      return;
    }

    const loadingKey = `${message.message_id}-${suggestionIndex}`;
    setApplyingChanges(loadingKey);

    try {
      // Extract platform campaign ID from the message
      const platformCampaignId = message.json_response?.details?.campaign_id ||
                                  message.json_response?.campaign_id ||
                                  campaign.platform_campaign_id;

      if (!platformCampaignId) {
        throw new Error('Platform campaign ID not found');
      }

      // Description + target entities + MadHive entity context. This page's
      // implementation was extracted verbatim into
      // utils/optimizerSuggestionApply so the campaign grid and the optimization
      // agent share it — including the keyword_targeting match-type grouping,
      // which the grid's own copy had been silently dropping.
      const built = buildSuggestionEditRequest(suggestion, {
        destination: campaign.destination,
        platformCampaignId,
      });
      if (built.error) throw new Error(built.error);
      const changeName = suggestion?.title || suggestion?.action_type
        || suggestion?.lever || 'this recommended change';
      if (!window.confirm(
        `Apply ${String(changeName).replace(/_/g, ' ')} to the live campaign?`,
      )) return;

      console.log('🎯 Applying changes:', {
        campaignId: campaignId,
        platformCampaignId: platformCampaignId,
        suggestion: suggestion,
        targetEntities: built.targetEntities,
        entityContext: built.entityContext,
        description: built.description
      });

      // Call v2 API endpoint to create edit message
      const response = await api.post(`/api/v2/campaigns/${campaignId}/edit`, {
        platformCampaignId: platformCampaignId,
        description: built.description,
        // Structured edits, when the suggestion carried a validated payload. The route's
        // batch path executes them directly instead of re-parsing the description with an
        // LLM, which is what dropped every edit after the first.
        ...(built.edits ? { edits: built.edits } : {}),
        // Google Ads creative operations ride through as a typed payload
        // rather than as prose for the editor's LLM to re-parse.
        ...(built.configPayload ? { configPayload: built.configPayload } : {}),
        targetEntities: built.targetEntities,
        entityContext: built.entityContext, // Pass full context for MadHive
        suggestedValues: built.suggestedValues,
        accountId: accountId,
        userId: user?.userId || user?.id,
        suggestionIndex: suggestionIndex, // Track which suggestion this edit is for
        optimizeMessageId: message.message_id // Track which optimize message this came from
      });

      if (response.data.success) {
        console.log('✅ Apply changes message created:', response.data.messageId);

        // Start polling for the new message
        if (response.data.messageId) {
          startMessagePolling(response.data.messageId);
        }

        // Refresh campaign messages to show the new message
        await fetchCampaignMessages();
      } else {
        throw new Error(response.data.error || 'Failed to create edit message');
      }
    } catch (error) {
      console.error('❌ Error applying changes:', error);
      alert(`Failed to apply changes: ${error.message}`);
    } finally {
      setApplyingChanges(null);
    }
  };

  // Handle edit campaign submission from modal
  const handleEditCampaign = async (description, selectedEntities = [], configPayload = null) => {
    setIsEditingCampaign(true);
    try {
      console.log('🎯 Editing campaign:', {
        campaignId: campaignId,
        description: description,
        selectedEntities: selectedEntities,
        editingCampaignId: editingCampaignId,
        campaign: campaign,
        orchestrationStatus: orchestrationStatus
      });

      // Use the editingCampaignId that was set when the Edit button was clicked
      // For TTD with multiple campaigns, configPayload.campaign_id or campaign_ids overrides the clicked ID
      const platformCampaignId = configPayload?.campaign_id
        || (configPayload?.campaign_ids?.length > 0 ? configPayload.campaign_ids[0] : null)
        || editingCampaignId;

      if (!platformCampaignId) {
        throw new Error('Platform campaign ID not found. Please make sure the campaign has been launched successfully.');
      }

      // Use selectedEntities from modal (already filtered by the modal for the specific campaign)
      const targetEntities = selectedEntities && selectedEntities.length > 0 ? selectedEntities : [];

      console.log('✅ Final IDs to send:', { platformCampaignId, targetEntities });

      // Call v2 API endpoint to create edit message
      const response = await api.post(`/api/v2/campaigns/${campaignId}/edit`, {
        platformCampaignId: platformCampaignId,
        description: description,
        targetEntities: targetEntities, // Pass ad group/ad set IDs
        suggestedValues: null,
        configPayload: configPayload, // JSON config for TTD deterministic ops
        accountId: accountId,
        userId: user?.userId || user?.id
      });

      if (response.data.success) {
        console.log('✅ Edit campaign message created:', response.data.messageId);

        // Start polling for the new message
        if (response.data.messageId) {
          startMessagePolling(response.data.messageId);
        }

        // Close modal
        setShowEditModal(false);

        // Refresh campaign messages to show the new message
        await fetchCampaignMessages();
      } else {
        throw new Error(response.data.error || 'Failed to create edit message');
      }
    } catch (error) {
      console.error('❌ Error editing campaign:', error);
      alert(`Failed to edit campaign: ${error.message}`);
    } finally {
      setIsEditingCampaign(false);
    }
  };

  // Platform display name - normalize platform names for display
  const getPlatformName = (platform) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_ads': 'Google Ads Search',
      'google-ads-search': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive',
      'groundtruth': 'GroundTruth',
      'youtube-dv360': 'YouTube DV360',
      'gam': 'Google Ad Manager'
    };
    return names[platform?.toLowerCase()] || platform || 'N/A';
  };

  // Helper to extract platform campaign ID from various sources (campaign record, orchestration result, etc.)
  // This mirrors the robust logic in UploadCreativesModal
  const getEffectivePlatformCampaignId = () => {
    if (!campaign) return null;
    
    // 1. Try direct campaign record
    if (campaign.platform_campaign_id) return campaign.platform_campaign_id;

    // 2. Try orchestration result
    const apiResult = orchestrationStatus?.apiResult;
    const details = apiResult?.details;
    
    if (!apiResult && !details) return null;

    const destination = campaign.destination?.toLowerCase();

    if (destination === 'dv360' || destination === 'youtube-dv360') {
      // Check for ID in URL if not found directly
      const dv360Url = details?.dv360_url || apiResult?.dv360_url;
      if (dv360Url) {
        const match = dv360Url.match(/\/a\/(\d+)\/c\/(\d+)/);
        if (match) return match[2];
      }
      return apiResult?.campaign_id || details?.campaign_id;
    } else if (destination === 'ttd') {
      // TTD ID might be in sandbox_url
      const sandboxUrl = details?.sandbox_url || (details?.sandbox_urls && details.sandbox_urls[0]);
      if (sandboxUrl) {
        const match = sandboxUrl.match(/\/campaign\/([^\/]+)/);
        if (match) return match[1];
      }
      return details?.campaign_id;
    } else if (destination === 'facebook') {
      // Check FB URL
      const fbUrl = details?.fb_url || apiResult?.fb_url;
      if (fbUrl) {
        const match = fbUrl.match(/selected_campaign_ids=(\d+)/);
        if (match) return match[1];
      }
      return details?.campaign_id || apiResult?.campaign_id;
    } else if (destination === 'reddit') {
      // Check Reddit URL
      const redditUrl = details?.reddit_url;
      if (redditUrl) {
        const match = redditUrl.match(/campaigns[:%]3A(\d+)/);
        if (match) return match[1];
      }
      // Check campaigns array
      const campaigns = details?.campaigns || apiResult?.campaigns;
      if (campaigns && campaigns.length > 0) return campaigns[0].id;
      
      return details?.campaign_id || apiResult?.campaign_id;
    } else if (destination === 'google-ads-search' || destination === 'google_search') {
      // Check campaigns array
      const campaigns = details?.campaigns || apiResult?.campaigns;
      if (campaigns && campaigns.length > 0) return campaigns[0].campaign_id;
      
      // Check legacy campaign_urls
      const campaignUrls = details?.campaign_urls || apiResult?.campaign_urls;
      if (campaignUrls && campaignUrls.length > 0) {
        const match = campaignUrls[0].match(/campaignId=([^&]+)/);
        if (match) return match[1];
      }
      return details?.campaign_id || apiResult?.campaign_id;
    } else if (destination === 'madhive') {
      return details?.campaign_id || 
             details?.campaign_results?.response?.id || 
             details?.processed_config?.lineItems?.[0]?.campaignId;
    }

    return null;
  };

  // Handle assign creatives from upload-creatives message
  const handleAssignCreatives = useCallback(async (uploadMessage) => {
    if (!campaign || !uploadMessage) {
      console.error('Missing campaign or upload message');
      alert('Unable to assign creatives: Missing required data');
      return;
    }

    try {
      // Extract data from upload message with robust fallbacks
      const jsonResponse = uploadMessage.json_response || {};
      
      // Advertiser ID: Check top-level, processing_info, or campaign record
      const advertiserId = jsonResponse.advertiser_id || 
                          jsonResponse.processing_info?.advertiser_id || 
                          campaign.advertiser_id;
                          
      // Platform: Check top-level or campaign destination
      let platform = jsonResponse.platform || campaign.destination;
      
      const uploadMessageId = uploadMessage.message_id;
      
      // Use robust ID extraction — every campaign the launch produced, since a
      // single insertion order can create several platform campaigns
      const platformCampaignIds = getPlatformCampaignIds(campaign, orchestrationStatus);
      const platformCampaignId = platformCampaignIds[0];

      if (!advertiserId || !platform || !platformCampaignId) {
        throw new Error('Missing required IDs for creative assignment (Campaign ID, Advertiser ID, or Platform)');
      }

      console.log('🎯 Assigning creatives:', {
        uploadMessageId,
        advertiserId,
        platform,
        platformCampaignId,
        platformCampaignIds,
        chatId: campaign.orchestration_chat_id
      });

      // Create assign-creatives message via chat API. The text lists every ID
      // so the LLM has full context for the other fields; the structured
      // fields below are what actually determine the campaigns.
      const campaignIdText = platformCampaignIds.length > 1
        ? `campaigns ${platformCampaignIds.join(', ')}`
        : `campaign ${platformCampaignId}`;
      const message = `Assign creatives from upload message ${uploadMessageId} to ${platform} ${campaignIdText} advertiser ${advertiserId}`;

      setAssigningMessageId(uploadMessageId);

      const response = await api.post('/api/chat', {
        message,
        chatId: campaign.orchestration_chat_id,
        campaignId: campaign.id,
        accountId,
        userId: user?.userId || user?.id,
        // Authoritative campaign IDs. Without these the dispatcher has to
        // re-extract them from `message` with the LLM, which collapses a
        // multi-campaign launch to whatever it manages to parse and has been
        // observed dropping characters from long alphanumeric IDs.
        extractedCampaignId: platformCampaignId,
        ...(platformCampaignIds.length > 1 && {
          extractedCampaignIds: JSON.stringify(platformCampaignIds)
        })
      });

      if (response.data.success || response.data.messageId) {
        console.log('✅ Assign-creatives message created:', response.data.messageId);

        // Start polling for the new message
        if (response.data.messageId) {
          startMessagePolling(response.data.messageId);
        }

        // Refresh campaign messages to show the new message
        await fetchCampaignMessages();
      } else {
        throw new Error(response.data.error || 'Failed to create assign-creatives message');
      }
    } catch (error) {
      console.error('❌ Error assigning creatives:', error);
      alert(`Failed to assign creatives: ${error.message}`);
    } finally {
      setAssigningMessageId(null);
    }
  }, [campaign, orchestrationStatus, accountId, user, startMessagePolling, fetchCampaignMessages]);

  // Expose handler to window for CampaignActivity to call
  useEffect(() => {
    window.onAssignCreatives = handleAssignCreatives;
    return () => {
      delete window.onAssignCreatives;
    };
  }, [handleAssignCreatives]);

  // Handle orchestration start
  const handleOrchestrationStart = (orchestrationData) => {
    console.log('🚀🚀🚀 handleOrchestrationStart called with:', orchestrationData);

    // Update campaign with orchestration IDs (optimistic update)
    setCampaign(prev => {
      const updated = {
        ...prev,
        orchestration_chat_id: orchestrationData.chatId,
        orchestration_message_id: orchestrationData.messageId,
        status: 'processing'
      };
      console.log('📝 Updated campaign state (optimistic):', {
        before: {
          orchestration_chat_id: prev?.orchestration_chat_id,
          orchestration_message_id: prev?.orchestration_message_id
        },
        after: {
          orchestration_chat_id: updated.orchestration_chat_id,
          orchestration_message_id: updated.orchestration_message_id
        }
      });
      return updated;
    });

    // Start polling for orchestration status
    if (orchestrationData.chatId && orchestrationData.messageId) {
      console.log('🎯 Calling startOrchestrationPolling with:', {
        chatId: orchestrationData.chatId,
        messageId: orchestrationData.messageId
      });
      startOrchestrationPolling(orchestrationData.chatId, orchestrationData.messageId);
    } else {
      console.error('❌ Missing chatId or messageId in orchestrationData');
    }

    // Refresh campaign details and messages to sync with database
    setTimeout(() => {
      console.log('🔄 Refetching campaign details and messages after orchestration start');
      fetchCampaignDetails(); // Refetch campaign to get updated file_count and other fields from database
      fetchCampaignMessages(); // Fetch messages to include the orchestration message
    }, 1000);
  };



  // Wait for context to load with timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!user || !accountId) {
        console.log('⏰ Context loading timeout - user or account not available');
        setWaitingForContext(false);
        setError('Unable to load user or account information. Please try refreshing the page or logging in again.');
        setIsLoading(false);
      } else {
        setWaitingForContext(false);
      }
    }, 5000); // 5 second timeout

    if (user && accountId) {
      clearTimeout(timeout);
      setWaitingForContext(false);
    }

    return () => clearTimeout(timeout);
  }, [user, accountId]);

  // Try to load campaign details immediately and when context becomes available
  useEffect(() => {
    // Always try to fetch - let the backend decide if it can handle partial context
    fetchCampaignDetails();
  }, [campaignId, accountId, user]);

  // Also try to fetch immediately when component mounts, even without full context
  useEffect(() => {
    if (campaignId && !campaign) {
      fetchCampaignDetails();
    }
  }, [campaignId]);

  // Poll activity feed periodically to catch new messages (e.g., from manual/auto pacing)
  useEffect(() => {
    if (!campaignId || !accountId || !user) return;

    const ACTIVITY_POLL_INTERVAL = 5000; // 5 seconds

    const activityPollInterval = setInterval(() => {
      console.log('🔄 Activity feed poll: refreshing campaign messages');
      fetchCampaignMessages({ skipAutoPolling: true });
    }, ACTIVITY_POLL_INTERVAL);

    return () => {
      clearInterval(activityPollInterval);
    };
  }, [campaignId, accountId, user]);

  // Cleanup polling interval on component unmount
  useEffect(() => {
    // Also add a beforeunload cleanup as a safety net
    const handleBeforeUnload = () => {
      cleanupPolling();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Use polling registry to cleanup all polling from this component
      pollingRegistry.cleanupByComponent('CampaignDetailPage');

      // Also call local cleanup
      cleanupPolling();

      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [campaignId]);

  // Additional cleanup when location changes (route-based cleanup)
  useEffect(() => {
    const currentPath = location.pathname;

    // If we're navigating away from this campaign detail page, cleanup
    if (!currentPath.includes(`/campaigns/${campaignId}`)) {
      cleanupPolling();
    }
  }, [location.pathname, campaignId]);

  // Start polling when campaign with orchestration IDs loads
  useEffect(() => {
    if (!campaign) return;

    // Wait for accountId and user to be available before attempting to start polling
    if (!accountId || !user) {
      console.log('⏳ Waiting for accountId and user before starting polling');
      return;
    }

    if (campaign.orchestration_chat_id && campaign.orchestration_message_id) {
      // Don't start if orchestration is already complete
      if (orchestrationStatus && ['completed', 'failed', 'stopped', 'timeout', 'review'].includes(orchestrationStatus.status)) {
        console.log('⏹️ Orchestration already complete, not starting polling');
        return;
      }

      // Don't start if already polling
      if (isPolling) {
        return; // Silent return - already polling
      }

      console.log('🚀 Campaign loaded with orchestration IDs, starting polling', {
        chatId: campaign.orchestration_chat_id,
        messageId: campaign.orchestration_message_id,
        hasAccountId: !!accountId,
        hasUser: !!user
      });
      startOrchestrationPolling(campaign.orchestration_chat_id, campaign.orchestration_message_id);
    }
  }, [campaign?.orchestration_chat_id, campaign?.orchestration_message_id, isPolling, orchestrationStatus?.status, accountId, user]);

  // Polling health check - aggressively restart polling if it stops
  useEffect(() => {
    // Don't run health check if orchestration is already complete/failed/stopped
    if (orchestrationStatus && ['completed', 'failed', 'stopped', 'timeout', 'review'].includes(orchestrationStatus.status)) {
      return;
    }

    // Don't run health check if accountId or user is not available
    if (!accountId || !user) {
      return;
    }

    const healthCheckInterval = setInterval(() => {
      const now = Date.now();
      const lastPoll = lastPollTimeRef.current;

      // If we haven't polled in 10 seconds, restart polling
      if (lastPoll && (now - lastPoll) > 10000) {
        console.log('⚠️ Polling stalled, restarting...');
        if (campaign?.orchestration_chat_id && campaign?.orchestration_message_id) {
          cleanupPolling();
          setTimeout(() => {
            startOrchestrationPolling(campaign.orchestration_chat_id, campaign.orchestration_message_id);
          }, 500);
        }
      }

      // If we're not polling but should be (campaign has orchestration IDs), start polling
      if (!isPolling && campaign?.orchestration_chat_id && campaign?.orchestration_message_id) {
        console.log('🔄 Health check: Starting polling for orchestration');
        startOrchestrationPolling(campaign.orchestration_chat_id, campaign.orchestration_message_id);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(healthCheckInterval);
  }, [isPolling, orchestrationStatus, campaign, accountId, user]);

  // Show loading state only if we don't have campaign data and are still loading
  if (isLoading && !campaign) {
    return (
      <div className="campaign-detail-loading">
        <p>Loading campaign details...</p>
        {waitingForContext && <p className="loading-detail">Please wait while we load your account information...</p>}
        {!user && <p className="loading-detail">Authenticating user...</p>}
        {user && !accountId && <p className="loading-detail">Loading account data...</p>}
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="campaign-detail-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>Error Loading Campaign</h3>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={retryContextLoad} className="btn btn-secondary">
            <FontAwesomeIcon icon={faRefresh} />
            Retry
          </button>
          <button onClick={() => {
            cleanupPolling();
            navigate('/campaigns');
          }} className="btn btn-primary">
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!campaign) {
    return (
      <div className="campaign-detail-not-found">
        <h3>Campaign Not Found</h3>
        <p>The requested campaign could not be found.</p>
        <button onClick={() => {
          cleanupPolling();
          navigate('/campaigns');
        }} className="btn btn-primary">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Campaigns
        </button>
      </div>
    );
  }

  // Helper function to check if optimize-campaign is enabled for the current platform
  // Gating shared with CampaignGridPage via utils/optimizeCampaignGating:
  // both the destination and every setting entry normalize to canonical
  // channel-type names, so 'g-ads-*' import-convention values and legacy
  // 'google-ads-*'/'google_*' values all match.
  const isOptimizeCampaignEnabled = () => optimizeEnabledFor(campaign, accountSettings);

  // Helper function to check if edit-campaign is enabled for the current platform
  const isEditCampaignEnabled = () => {
    if (!campaign || !accountSettings) return false;
    // Both the setting entries and the destination are canonicalized.
    return isEnabledForPlatform(accountSettings['edit-campaign'], campaign.destination);
  };

  // Helper function to check if refresh-campaign is enabled for the current platform
  const isRefreshCampaignEnabled = () => {
    if (!campaign || !accountSettings) return false;
    // Both the setting entries and the destination are canonicalized.
    return isEnabledForPlatform(accountSettings['refresh-campaign'], campaign.destination);
  };

  // "Create Geofence Ad Groups" button gate — TTD-only, behind the
  // `geofence-adgroups` account setting (platform-list value, e.g. 'ttd').
  const isGeofenceAdGroupsEnabled = () => {
    if (!campaign || !accountSettings) return false;
    if (campaign.destination?.toLowerCase() !== 'ttd') return false;
    return isEnabledForPlatform(accountSettings['geofence-adgroups'], 'ttd');
  };

  // FB daily budget alert history — Facebook imports only, behind the
  // `enable-import-campaign-fb-daily-budget-alert` account setting. Accepts
  // both value conventions: boolean 'true' or a platform list containing
  // facebook/fb/meta (mirrors the backend fbBudgetAlertFlag helper).
  const isFbBudgetAlertEnabled = () => {
    if (!campaign || !accountSettings) return false;
    const destination = campaign.destination?.toLowerCase();
    if (!['facebook', 'fb', 'meta'].includes(destination)) return false;
    const setting = accountSettings['enable-import-campaign-fb-daily-budget-alert'];
    if (!setting) return false;
    if (setting.trim().toLowerCase() === 'true') return true;
    return ['facebook', 'fb', 'meta'].some(p => isEnabledForPlatform(setting, p));
  };

  // Helper function to check if auto-pacing is enabled for the current platform
  const isAutoPacingEnabled = () => {
    if (!campaign || !accountSettings) return false;
    // Both the setting entries and the destination are canonicalized.
    return isEnabledForPlatform(accountSettings['auto-pacing'], campaign.destination);
  };

  // Helper function to check if creative scheduling is enabled for the current platform
  const isCreativeSchedulingEnabled = () => {
    if (!campaign || !accountSettings) return false;
    // Both the setting entries and the destination are canonicalized.
    return isEnabledForPlatform(accountSettings['creative-scheduling'], campaign.destination);
  };

  // Helper function to check if ad group scheduling is enabled (TTD only).
  // Gated by the separate 'adgroup-scheduling' account setting (platform CSV).
  const isAdGroupSchedulingEnabled = () => {
    if (!campaign || !accountSettings) return false;

    const destination = campaign.destination?.toLowerCase();
    const adGroupSchedulingSetting = accountSettings['adgroup-scheduling'];

    if (!adGroupSchedulingSetting) return false;

    const enabledPlatforms = adGroupSchedulingSetting
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(p => p.length > 0);

    // TTD is the only platform supported today; normalize alternate spellings.
    let normalizedDestination = destination;
    if (destination === 'the-trade-desk' || destination === 'thetradedesk') {
      normalizedDestination = 'ttd';
    }

    return enabledPlatforms.includes(normalizedDestination);
  };

  // Helper function to get platform URLs for Campaign Actions
  const getPlatformUrls = () => {
    if (!campaign) {
      return null;
    }

    const urls = [];
    const destination = campaign.destination?.toLowerCase();

    // Check for import-campaign message first (for imported campaigns)
    const importMessage = campaignMessages.find(msg =>
      msg.action === 'import-campaign' || msg.message_type === 'import-campaign'
    );
    const importResult = importMessage?.json_response;
    const importApiResult = importResult?.api_result;

    console.log('🔍 getPlatformUrls - destination:', destination);
    console.log('🔍 getPlatformUrls - campaign:', campaign);
    console.log('🔍 getPlatformUrls - importResult:', importResult);
    console.log('🔍 getPlatformUrls - importApiResult:', importApiResult);

    // If this is an import-campaign, extract campaign data from import result
    if (importApiResult?.success && importApiResult?.campaigns?.length > 0) {
      console.log('🔍 Found import-campaign with campaigns:', importApiResult.campaigns);

      // Get platform from the import response (normalize platform name)
      const importPlatform = importResult?.platform ||
                            importApiResult?.platform?.replace('-ads', '') ||
                            destination;

      // Add each imported campaign to the URLs array
      importApiResult.campaigns.forEach((importedCampaign) => {
        if (importedCampaign.campaign_url) {
          urls.push({
            label: `View Campaign`,
            url: importedCampaign.campaign_url,
            campaign_id: importedCampaign.campaign_id,
            campaign_name: importedCampaign.name,
            supportsOptimize: isOptimizeCampaignEnabled(),
            isImported: true // Flag to indicate this is from import-campaign
          });
        }
      });

      // If we found import campaigns, return early
      if (urls.length > 0) {
        console.log('🔍 getPlatformUrls - returning import campaign urls:', urls);
        return urls;
      }
    }

    // Use orchestration result or import result
    const apiResult = orchestrationStatus?.apiResult || importApiResult;
    const details = apiResult?.details || importApiResult;

    console.log('🔍 getPlatformUrls - apiResult:', apiResult);

    // Get platform-specific URLs - check both campaign object and apiResult
    if (destination === 'dv360' || destination === 'youtube-dv360') {
      const campaignUrl = campaign.campaign_url || details?.dv360_url || apiResult?.dv360_url || importResult?.dv360_url;
      let campaignId = campaign.platform_campaign_id || details?.campaign_id || apiResult?.campaign_id;

      // Crimtan Market Expansion launches one campaign PER MARKET inside a single
      // message, so `details.dv360_url` — one string — is not the whole story.
      // details.market_expansion_campaigns carries every campaign with the market
      // that produced it; prefer it so each market gets its own button, the way
      // Yahoo and TTD already do for their multi-campaign launches.
      const marketCampaigns = (details?.market_expansion_campaigns
        || apiResult?.details?.market_expansion_campaigns || [])
        .filter(c => c?.dv360_url)
        .map(c => ({ url: c.dv360_url, campaign_id: c.campaign_id, name: c.market }));

      const campaignUrls = campaign.campaign_urls
        || (marketCampaigns.length > 1 ? marketCampaigns : null);

      // Checked BEFORE the single-url branch: `campaignUrl` is always set on a
      // market-expansion launch (it is the primary market), so testing it first
      // showed one button and hid every other market.
      if (campaignUrls && Array.isArray(campaignUrls) && campaignUrls.length > 1) {
        // For multiple campaigns, extract campaign IDs from URLs
        const campaignsData = campaignUrls.map(urlObj => {
          const url = typeof urlObj === 'string' ? urlObj : urlObj.url;
          const match = url.match(/\/c\/(\d+)/);
          return {
            url: url,
            campaign_id: (typeof urlObj === 'object' && urlObj.campaign_id)
              || (match ? match[1] : null),
            ...(typeof urlObj === 'object' && urlObj.name ? { name: urlObj.name } : {})
          };
        });

        urls.push({
          label: destination === 'youtube-dv360' ? 'View in YouTube DV360' : 'View in DV360',
          urls: campaignUrls,
          campaigns: campaignsData,
          multiple: true,
          supportsOptimize: isOptimizeCampaignEnabled(),
          isImported: !!importMessage
        });
      } else if (campaignUrl) {
        // Extract campaign ID from URL if not already available
        let extractedCampaignId = campaignId;
        if (!extractedCampaignId) {
          const match = campaignUrl.match(/\/c\/(\d+)/);
          if (match) extractedCampaignId = match[1];
        }

        urls.push({
          label: destination === 'youtube-dv360' ? 'View in YouTube DV360' : 'View in DV360',
          url: campaignUrl,
          campaign_id: extractedCampaignId,
          supportsOptimize: isOptimizeCampaignEnabled(),
          isImported: !!importMessage
        });
      } else if (importMessage && campaignId) {
        // No URL available yet but import exists — still show action buttons
        urls.push({
          label: destination === 'youtube-dv360' ? 'View in YouTube DV360' : 'View in DV360',
          url: null,
          campaign_id: campaignId,
          supportsOptimize: isOptimizeCampaignEnabled(),
          isImported: true
        });
      }
    } else if (destination === 'ttd') {
      const sandboxUrl = campaign.sandbox_url || details?.sandbox_url || apiResult?.sandbox_url || importResult?.sandbox_url;
      const sandboxUrls = campaign.sandbox_urls || details?.sandbox_urls || apiResult?.sandbox_urls || importResult?.sandbox_urls;

      if (sandboxUrl) {
        // Single campaign — extract campaign ID from URL
        const match = sandboxUrl.match(/\/campaign\/([^\/]+)/);
        urls.push({
          label: 'View in TTD',
          url: sandboxUrl,
          campaign_id: match ? match[1] : campaign.platform_campaign_id,
          supportsOptimize: isOptimizeCampaignEnabled(),
          isImported: !!importMessage
        });
      } else if (sandboxUrls && Array.isArray(sandboxUrls) && sandboxUrls.length > 0) {
        // Multiple campaigns — extract each campaign ID from URLs (structured format)
        // Build campaign_id → name map from campaign_results.details[] (each entry has details.CampaignId and details.CampaignName)
        const campaignResultDetails = apiResult?.campaign_results?.details || details?.campaign_results?.details || importResult?.campaign_results?.details || [];
        const campaignNameMap = {};
        campaignResultDetails.forEach(cr => {
          const d = cr?.details || cr || {};
          const cId = d.CampaignId || d.campaignId;
          const cName = d.CampaignName || d.campaignName;
          if (cId && cName) campaignNameMap[cId] = cName;
        });
        // Also check the new campaign_id_to_name mapping as fallback
        const campaignIdToName = apiResult?.campaign_results?.campaign_id_to_name || details?.campaign_results?.campaign_id_to_name || importResult?.campaign_results?.campaign_id_to_name || {};
        Object.assign(campaignNameMap, { ...campaignIdToName, ...campaignNameMap }); // existing details take priority

        const campaigns = sandboxUrls.map(url => {
          const match = url.match(/\/campaign\/([^\/]+)/);
          const cId = match ? match[1] : null;
          return {
            url,
            campaign_id: cId,
            name: (cId && campaignNameMap[cId]) ? campaignNameMap[cId] : null
          };
        }).filter(c => c.campaign_id);

        if (campaigns.length > 0) {
          urls.push({ label: 'View in TTD', campaigns, multiple: true, supportsOptimize: isOptimizeCampaignEnabled(), isImported: !!importMessage });
        } else {
          urls.push({ label: 'View in TTD', urls: sandboxUrls, multiple: true, supportsOptimize: isOptimizeCampaignEnabled(), isImported: !!importMessage });
        }
      } else if (importMessage) {
        // No sandbox URL — build campaign list from import result campaigns dict
        const importCampaignsDict = importApiResult?.campaigns;
        if (importCampaignsDict && typeof importCampaignsDict === 'object' && !Array.isArray(importCampaignsDict)) {
          const campaignEntries = Object.entries(importCampaignsDict)
            .filter(([, cdata]) => cdata?.success !== false)
            .map(([cid, cdata]) => ({
              url: null,
              campaign_id: cid,
              name: cdata?.summary?.campaign_name || cdata?.campaign_data?.campaign_name || null
            }));
          if (campaignEntries.length > 1) {
            urls.push({ label: 'View in TTD', campaigns: campaignEntries, multiple: true, supportsOptimize: isOptimizeCampaignEnabled(), isImported: true });
          } else if (campaignEntries.length === 1) {
            urls.push({ label: 'View in TTD', url: null, campaign_id: campaignEntries[0].campaign_id, supportsOptimize: isOptimizeCampaignEnabled(), isImported: true });
          }
        } else if (campaign.platform_campaign_id) {
          urls.push({ label: 'View in TTD', url: null, campaign_id: campaign.platform_campaign_id, supportsOptimize: isOptimizeCampaignEnabled(), isImported: true });
        }
      }
    } else if (destination === 'facebook') {
      // Facebook URLs come from orchestration result, not campaign record
      const facebookUrl = details?.fb_url || apiResult?.fb_url;
      let campaignId = campaign.platform_campaign_id || details?.campaign_id || apiResult?.campaign_id;

      console.log('🔍 Facebook URL check:', { facebookUrl, campaignId, details, apiResult });

      if (facebookUrl) {
        // Extract campaign ID from URL if not already available
        let extractedCampaignId = campaignId;
        if (!extractedCampaignId) {
          const match = facebookUrl.match(/selected_campaign_ids=(\d+)/);
          if (match) extractedCampaignId = match[1];
        }

        console.log('🔍 Facebook campaign ID:', extractedCampaignId);

        urls.push({
          label: 'View in Facebook',
          url: facebookUrl,
          campaign_id: extractedCampaignId,
          supportsOptimize: isOptimizeCampaignEnabled()
        });
      }
    } else if (destination === 'reddit') {
      // Reddit URLs come from orchestration result, not campaign record
      const redditUrl = details?.reddit_url || apiResult?.reddit_url;

      // Try to get campaign ID from multiple sources
      let campaignId = campaign.platform_campaign_id || details?.campaign_id || apiResult?.campaign_id;

      // If not found, try to get from campaigns array
      if (!campaignId) {
        const campaigns = details?.campaigns || apiResult?.campaigns || [];
        if (campaigns.length > 0) {
          campaignId = campaigns[0].id;
        }
      }

      if (redditUrl) {
        // Extract campaign ID from URL if still not available
        let extractedCampaignId = campaignId;
        if (!extractedCampaignId) {
          const match = redditUrl.match(/campaigns[:%]3A(\d+)/);
          if (match) {
            extractedCampaignId = match[1];
          }
        }

        console.log('🔍 Reddit campaign ID:', extractedCampaignId);

        urls.push({
          label: 'View in Reddit',
          url: redditUrl,
          campaign_id: extractedCampaignId,
          supportsOptimize: isOptimizeCampaignEnabled()
        });
      }
    } else if (
      ['google-ads-search', 'google-ads-pmax', 'google-ads-demandgen']
        .includes(canonicalPlatform(destination))
    ) {
      // Google Ads URLs come from orchestration result, not campaign record.
      // Search, PMax and Demand Gen share this branch because their creators
      // return the same shape — a `campaigns` array whose successful entries
      // carry campaign_url / campaign_id / name. See pmax_campaign_creator.py
      // (~L280) and demandgen_campaign_creator.py (~L771). Without PMax and
      // Demand Gen here they fell through every branch, so getPlatformUrls
      // returned null and CampaignCompletionStatus rendered no action buttons
      // at all — not just no View Campaign.
      //
      // Matched through canonicalPlatform rather than a literal list because
      // the destination reaches us under several spellings (google_pmax,
      // g-ads-pmax, pmax, ...) — hardcoding one of them is what made this
      // branch miss PMax to begin with.
      const googleAdsUrl = details?.campaign_url || apiResult?.campaign_url;
      const campaigns = details?.campaigns || apiResult?.campaigns;

      if (googleAdsUrl) {
        urls.push({ label: 'View in Google Ads', url: googleAdsUrl });
      } else if (campaigns && Array.isArray(campaigns) && campaigns.length > 0) {
        // Include campaign_id with each URL for optimize functionality.
        // Failed entries (e.g. PMax gate failures) carry no campaign_url and
        // are filtered out here.
        const campaignData = campaigns
          .filter(c => c.campaign_url)
          .map(c => ({
            url: c.campaign_url,
            campaign_id: c.campaign_id,
            name: c.name
          }));
        if (campaignData.length > 0) {
          urls.push({
            label: 'View in Google Ads',
            campaigns: campaignData,
            multiple: true,
            supportsOptimize: isOptimizeCampaignEnabled()
          });
        }
      }
    } else if (destination === 'madhive') {
      // Madhive URLs come from orchestration result details
      const madhiveUrl = campaign.campaign_url || details?.madhive_url || apiResult?.madhive_url;
      let campaignId = campaign.platform_campaign_id || details?.campaign_id || apiResult?.campaign_id;

      if (madhiveUrl) {
        // Extract campaign ID from URL if not already available
        let extractedCampaignId = campaignId;
        if (!extractedCampaignId) {
          // Try to extract from URL if possible (MadHive URL format may vary)
          const match = madhiveUrl.match(/campaign[\/=](\d+)/i);
          if (match) extractedCampaignId = match[1];
        }

        urls.push({
          label: 'View in Madhive',
          url: madhiveUrl,
          campaign_id: extractedCampaignId,
          supportsOptimize: isOptimizeCampaignEnabled()
        });
      }
    } else if (destination === 'amazon-dsp' || destination === 'amazon_dsp') {
      // Amazon DSP results - display campaign IDs and success status
      const campaignDetails = apiResult?.campaign_details;
      const success = apiResult?.success;

      if (success && campaignDetails) {
        const successList = campaignDetails?.success || [];
        if (successList.length > 0) {
          const campaignIds = successList.map(c => c.campaignId).filter(Boolean);
          if (campaignIds.length > 0) {
            const amazonDspUrl = `https://advertising.amazon.com/dsp/campaigns`;
            urls.push({
              label: 'View in Amazon DSP',
              url: amazonDspUrl,
              campaign_id: campaignIds[0],
              supportsOptimize: false
            });
          }
        }
      }
    } else if (destination === 'yahoo' || destination === 'yahoo-dsp' || destination === 'yahoo_dsp') {
      // Yahoo DSP results - campaign ids come from details.campaign_ids.
      // Shape read off a real launcher result, not assumed:
      //   { success, details: { campaign_ids: [9001], line_ids: [7001, 7002],
      //                         campaigns: [{ campaign_id, campaign_name, lines: [...] }] } }
      // Reported even when success is false: Yahoo has no delete API, so a
      // partially-created campaign still exists and the operator needs the link
      // to go pause it by hand.
      const yahooCampaigns = (apiResult?.details?.campaigns || [])
        .filter(c => c?.campaign_id);
      const yahooCampaignIds = yahooCampaigns.length > 0
        ? yahooCampaigns.map(c => c.campaign_id)
        : (apiResult?.details?.campaign_ids || []).filter(Boolean);

      // The Yahoo client covers campaign structure only — no reporting fetch —
      // so there is nothing for the optimizer to reason over.
      const yahooLinkProps = { label: 'View in Yahoo DSP', supportsOptimize: false };

      if (yahooCampaignIds.length === 1) {
        urls.push({
          ...yahooLinkProps,
          url: buildYahooCampaignUrl(yahooCampaignIds[0]),
          campaign_id: yahooCampaignIds[0]
        });
      } else if (yahooCampaignIds.length > 1) {
        // One IO can produce several Yahoo campaigns; linking only to the first
        // hid the rest, and with no delete API the operator has to reach every
        // one of them by hand.
        urls.push({
          ...yahooLinkProps,
          campaigns: yahooCampaignIds.map((id, index) => ({
            url: buildYahooCampaignUrl(id),
            campaign_id: id,
            name: yahooCampaigns[index]?.campaign_name
          })),
          multiple: true
        });
      }
    } else if (destination === 'gam' || destination === 'gam-api' || destination === 'google-ad-manager') {
      // Google Ad Manager. Shape captured from a real create_order return
      // (gam_api/gam_order_creator.py) rather than assumed — and note it is NOT
      // wrapped in `details` the way the Yahoo and Amazon results are:
      //   { order_id: 3711204195, order_name: "...", line_items: [{ line_item_id, name }] }
      // The GAM UI addresses an order by network code, which lives on the channel
      // config rather than the launch result, so the link goes to the orders list
      // and carries the order id for the operator to match.
      const gamOrderId = apiResult?.order_id || apiResult?.details?.order_id || null;

      if (gamOrderId) {
        urls.push({
          label: 'View in Google Ad Manager',
          url: 'https://admanager.google.com/',
          campaign_id: String(gamOrderId),
          // gam_api creates orders and line items; its pull-report still returns a
          // checked-in sample rather than a real ReportService read, so there is no
          // delivery data for the optimizer to reason over.
          supportsOptimize: false
        });
      }
    }

    console.log('🔍 getPlatformUrls - urls:', urls);
    return urls.length > 0 ? urls : null;
  };

  // A GroundTruth IO launch opens as one workspace: the Campaign grid (actual
  // GT state) and the Activity log (everything below). The view is in the URL
  // so reload, back/forward and shared links land on the same one; without
  // one, a clean launch opens on the grid, and an in-flight, failed or partial
  // launch (success with errors) on its activity, where its errors are.
  const gtLaunch = gtLaunchStateFromMessages(campaign, campaignMessages);
  const requestedView = searchParams.get('view');
  const gtView = !gtLaunch ? null
    : (requestedView === 'activity' || requestedView === 'grid')
      ? requestedView
      : (gtLaunch === 'launched' ? 'grid' : 'activity');
  const setGtView = (view) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    setSearchParams(next);
  };

  return (
    <div className="campaign-detail-page">
      {/* Header */}
      <div className="campaign-detail-header">
        <button
          onClick={() => {
            cleanupPolling();
            navigate('/campaigns');
          }}
          className="back-button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Campaigns
        </button>
        
        <div className="campaign-info">
          <h1>{campaign.name}</h1>
          <div className="campaign-meta">
            <span className="destination">{getPlatformName(campaign.destination)}</span>
            <span className="folder">{campaign.folder_name}</span>
            {isEnabledForPlatform(accountSettings?.['enable-mmgy-io-type'], campaign.destination) && campaign.io_type && (
              <span className="io-type-badge">{campaign.io_type.toUpperCase()}</span>
            )}
            {(campaign.advertiser_id || orchestrationStatus?.advertiserId || orchestrationStatus?.adAccountId) && (
              <span className="advertiser">
                {campaign.destination === 'reddit' ? 'Profile ID' :
                 campaign.destination === 'groundtruth' ? 'Account ID' :
                 (campaign.destination === 'facebook' || campaign.destination === 'linkedin') ? 'Ad Account' :
                 'Advertiser'}: {campaign.advertiser_id || orchestrationStatus?.adAccountId || orchestrationStatus?.advertiserId}
              </span>
            )}
            {orchestrationStatus?.planningMode && orchestrationStatus?.status === 'review' && (
              <span className="planning-mode-badge">PLANNING MODE</span>
            )}
            {/* The row stays 'draft' after a GT IO launch; the launch itself is the truth. */}
            <StatusBadge status={gtLaunch === 'launched' ? 'launched'
              : gtLaunch === 'launched_partial' ? 'completed_with_errors' : campaign.status} />
          </div>
        </div>

        {accountSettings?.['enable-update-creative'] === 'true' && canSendMessage(user)
          && /google|g-ads/.test(campaign.destination || '')
          && hasChannelAccess(user, campaign.destination) && (
          <UpdateCreativeButton campaignId={campaignId} accountId={accountId} userId={user?.userId || user?.id}
            onClick={() => { cleanupPolling(); navigate(`/campaigns/${campaignId}/update-creative`); }} />
        )}

        {/* Open the same campaign in the new Sidekick CLW. Available for any
            campaign that has produced a parsed launcher result — that's:
              • planning-mode campaigns (orchestrationStatus.planningMode === true)
                — they can be opened in 'review' state (still launchable) or
                after launch (read-only).
              • fast-track campaigns whose API step completed — campaign.status
                lands on 'ready_to_launch' / 'awaiting_creatives' / 'completed'
                / 'live' / etc., all with a launcher_result_path on GCS.
              • imported campaigns (created via import-campaign) — the
                planning-review endpoint derives a launcherResult from the
                import message, and CLW renders it like a launched campaign
                (summary + post-launch grid edits).
            CLW hydrates from ?campaign=<id>, calls /api/v2/campaigns/:id/planning-review,
            and renders the summary pane in read-only mode when the orchestration is
            no longer in 'review' status (Launch button stays disabled). */}
        {(() => {
          if (!v3UiEnabled && !(planningGridForAllRoles && orchestrationStatus?.planningMode === true)) return null;
          const launcherResultStatuses = new Set([
            'ready_to_launch', 'awaiting_creatives', 'awaiting_download',
            'completed', 'active', 'paused', 'live', 'ready',
          ]);
          const hasLauncherResult = Boolean(orchestrationStatus?.planningMode)
            || (campaign?.status && launcherResultStatuses.has(campaign.status));
          if (!hasLauncherResult) return null;
          return (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginLeft: 'auto', alignSelf: 'flex-start' }}
              onClick={() => {
                cleanupPolling();
                navigate(`/campaign/v3/start?campaign=${encodeURIComponent(campaignId)}`);
              }}
              title="Open this campaign in the new Sidekick UI (summary + review)"
            >
              Open in new UI
            </button>
          );
        })()}
      </div>

      {gtLaunch && (
        <GtLaunchedWorkspace
          campaignId={campaignId}
          accountId={accountId}
          userId={user?.userId || user?.id}
          launchState={gtLaunch}
          view={gtView}
          onViewChange={setGtView}
          campaignName={campaign.name}
        />
      )}

      {/* Everything from here to the activity list is the Activity log view of
          a GT launch workspace; parked (not unmounted) while the grid shows.
          display:contents keeps these sections the page's own flex items, so
          spacing on every other campaign page is unchanged. */}
      <div className="campaign-detail-activity-view" style={{ display: gtView === 'grid' ? 'none' : 'contents' }}>

      {/* Asset Processing Notification */}
      <AssetProcessingNotification
        campaign={campaign}
      />

      {/* Planning Mode Review - shown when orchestration pauses for user review */}
      {orchestrationStatus?.status === 'review' && orchestrationStatus?.planningMode && (
        <CampaignPlanningReview
          campaignId={campaignId}
          accountId={accountId}
          userId={user?.userId || user?.id}
          previousApiError={orchestrationStatus?.apiResult?.success === false ? (orchestrationStatus.apiResult.error_message || orchestrationStatus.apiResult.error || 'Campaign launch failed') : null}
          onLaunch={() => {
            // After launching, restart polling to track API step
            setOrchestrationStatus(prev => ({ ...prev, status: 'processing', currentStep: 'api' }));
            if (campaign?.orchestration_chat_id && campaign?.orchestration_message_id) {
              startOrchestrationPolling(campaign.orchestration_chat_id, campaign.orchestration_message_id);
            }
          }}
          onBack={() => navigate('/campaigns')}
        />
      )}

      {/* Failed/Stopped Planning Mode - offer to return to review only if launcher step completed
           (not during campaign-processor or campaign-launcher stages, which haven't produced a reviewable result) */}
      {orchestrationStatus?.planningMode && orchestrationStatus?.stepProgress?.launcher?.status === 'completed' && (
        orchestrationStatus?.status === 'stopped' ||
        orchestrationStatus?.status === 'failed' ||
        (orchestrationStatus?.status === 'completed' && orchestrationStatus?.apiResult?.success === false) ||
        (orchestrationStatus?.stepProgress?.api?.status === 'failed')
      ) && (
        <div className="planning-failed-banner" style={{ padding: '16px', margin: '16px 0', background: '#fef3cd', border: '1px solid #ffc107', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="material-symbols-outlined" style={{ color: '#856404' }}>warning</span>
          <div style={{ flex: 1 }}>
            <strong>{orchestrationStatus?.status === 'stopped' ? 'Campaign was stopped.' : 'Campaign launch failed.'}</strong> You can return to review mode to adjust settings and try again.
          </div>
          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/planning-reset`), {
                  accountId,
                  userId: user?.userId || user?.id
                });
                // Refresh orchestration status to show review UI
                if (campaign?.orchestration_chat_id && campaign?.orchestration_message_id) {
                  startOrchestrationPolling(campaign.orchestration_chat_id, campaign.orchestration_message_id);
                }
              } catch (err) {
                console.error('Error resetting to review:', err);
              }
            }}
          >
            Return to Review
          </button>
        </div>
      )}

      {/* Response Message - matching mockup */}
      {orchestrationStatus && orchestrationStatus.status !== 'review' && (
        <div className="response-message">
          <p>Sure. Processing campaign now.</p>
        </div>
      )}

      {/* Success message removed per user request */}

      {/* Campaign Completion Status - Show for orchestration OR single messages like upload-creatives OR import-campaign */}
      {(() => {
        // Check if there are any single messages (upload-creatives, assign-creatives, import-campaign) - processing or completed
        const singleMessages = campaignMessages.filter(msg =>
          ['upload-creatives', 'import-campaign'].includes(msg.message_type) ||
          ['import-campaign'].includes(msg.action)
        );

        // Show if there's orchestration OR any single messages exist OR if platformUrls are available
        const platformUrls = getPlatformUrls();
        const shouldShow = orchestrationStatus || isLoading || singleMessages.length > 0 || platformUrls;

        return shouldShow && (
          <CampaignCompletionStatus
            campaign={campaign}
            orchestrationStatus={orchestrationStatus}
            campaignMessages={campaignMessages}
            isLoading={isLoading}
            onStopOrchestration={handleStopOrchestration}
            onStopMessage={handleStopMessage}
            onUploadCreatives={uploadCreativesHidden ? null : handleUploadCreatives}
            uploadCreativesData={uploadCreativesData}
            accountId={accountId}
            userId={user?.userId || user?.id}
            onCampaignUpdate={fetchCampaignDetails}
            onOrchestrationStart={handleOrchestrationStart}
            onOptimizeCampaign={isOptimizeCampaignEnabled() ? handleOptimizeCampaign : null}
            optimizingAnalysisKind={optimizingAnalysisKind}
            isOptimizing={isOptimizing}
            onRefreshCampaign={isRefreshCampaignEnabled() ? handleRefreshCampaign : null}
            onRunAudit={handleRunAudit}
            isAuditing={isAuditing}
            isRefreshing={isRefreshing}
            platformUrls={platformUrls}
            onEditCampaign={isEditCampaignEnabled() ? (platformCampaignId) => {
              setEditingCampaignId(platformCampaignId);
              setShowEditModal(true);
            } : null}
            onCreateGeofenceAdGroups={isGeofenceAdGroupsEnabled() ? handleCreateGeofenceAdGroups : null}
            isCreatingGeofence={isCreatingGeofence}
          />
        );
      })()}



      {/* Current Status Message - matching mockup */}
      {/* {orchestrationStatus && orchestrationStatus.status !== 'completed' && (
        <div className="current-status-message">
          <FontAwesomeIcon icon={faClock} />
          <span>{getCurrentStatusMessage(orchestrationStatus)}</span>
        </div>
      )} */}

      {/* Time Estimate - matching mockup */}
      {/* {orchestrationStatus && orchestrationStatus.status !== 'completed' && (
        <div className="time-estimate">
          <p>Estimated time remaining: {getTimeEstimate(orchestrationStatus)}</p>
        </div>
      )} */}

      {/* Note: Removed CampaignCompletionMessage - not needed in campaign details page */}

      {/* Campaign Action Buttons */}
      {/* {orchestrationStatus?.status === 'completed' && !isLoading && (
        <CampaignActionButtons
          campaign={campaign}
          orchestrationStatus={orchestrationStatus}
          onGoToPlatform={handleGoToPlatform}
          onViewPerformance={handleViewPerformance}
          onViewOptimization={handleViewOptimization}
        />
      )} */}

      {/* Auto Pacing Section - only show for campaigns with platform_campaign_id, if enabled for platform, and if user has channel access */}
      {campaign?.platform_campaign_id && isAutoPacingEnabled() && hasChannelAccess(user, campaign.destination) && (
        <AutoPacingScheduleList
          campaign={campaign}
          accountId={accountId}
          user={user}
        />
      )}

      {/* Creative Scheduling Section - only show for campaigns with platform_campaign_id, if enabled for platform, and if user has channel access */}
      {campaign?.platform_campaign_id && isCreativeSchedulingEnabled() && hasChannelAccess(user, campaign.destination) && (
        <CreativeScheduleList
          campaign={campaign}
          accountId={accountId}
          user={user}
        />
      )}

      {/* Ad Group Scheduling Section - TTD only, gated by the adgroup-scheduling account setting */}
      {campaign?.platform_campaign_id && isAdGroupSchedulingEnabled() && hasChannelAccess(user, campaign.destination) && (
        <AdGroupScheduleList
          campaign={campaign}
          accountId={accountId}
          user={user}
        />
      )}

      {/* FB Daily Budget Alerts Section - Facebook imports only, gated by the
          enable-import-campaign-fb-daily-budget-alert account setting */}
      {campaign?.platform_campaign_id && isFbBudgetAlertEnabled() && hasChannelAccess(user, campaign.destination) && (
        <FbBudgetAlertHistory
          campaign={campaign}
          accountId={accountId}
          user={user}
        />
      )}

      {/* Retargeting Reach Watch Section - MadHive only. Watches are created
          automatically when a launch carries retargeting fields; the component
          renders nothing when the campaign has no watch, so no account-setting
          gate is needed. */}
      {campaign?.destination?.toLowerCase() === 'madhive' && hasChannelAccess(user, campaign.destination) && (
        <RetargetingWatchSection
          campaign={campaign}
          accountId={accountId}
          user={user}
        />
      )}

      {/* Upload Creatives Modal */}
      {/* Campaign Activity Section */}
      <CampaignActivity
        messages={campaignMessages}
        isLoading={isLoading}
        onApplyChanges={handleApplyChanges}
        applyingChanges={applyingChanges}
        campaign={campaign}
        platformCampaignId={getEffectivePlatformCampaignId()}
        isSuggestionApplied={isSuggestionApplied}
        appliedImpacts={appliedImpacts}
        onStopMessage={handleStopMessage}
        assigningMessageId={assigningMessageId}
        accountId={accountId}
        userId={user?.userId || user?.id}
        onOptimizeCampaign={isOptimizeCampaignEnabled() ? handleOptimizeCampaign : null}
        optimizingAnalysisKind={optimizingAnalysisKind}
        onRefreshCampaign={isRefreshCampaignEnabled() ? handleRefreshCampaign : null}
            onRunAudit={handleRunAudit}
            isAuditing={isAuditing}
        onEditCampaign={isEditCampaignEnabled() ? (platformCampaignId) => {
          setEditingCampaignId(platformCampaignId);
          setShowEditModal(true);
        } : null}
        isOptimizing={isOptimizing}
        isRefreshing={isRefreshing}
        accountSettings={accountSettings}
        onUploadCreatives={uploadCreativesHidden ? null : handleUploadCreatives}
        canDownloadResultZip={campaignResultZipEnabled}
        superAdminUserId={user?.isImpersonating ? user?.impersonatedBy : (user?.userId || user?.id)}
        // Raw response JSON + its download: super admins always (including
        // while impersonating), plus every role on accounts that opted in via
        // `enable-result-section`. CampaignActivity reads that same setting to
        // decide whether to keep the "Super admin only" notice honest.
        canViewRawJson={canViewRawJson}
        hideHighOperationCountWarnings={orchestrationStatus?.copyWorkflow === true}
        onActivityChanged={fetchCampaignMessages}
      />

      {gtLaunch && gtView === 'activity' && (
        <GtLaunchHistory campaignId={campaignId} accountId={accountId} userId={user?.userId || user?.id} />
      )}
      </div>

      <UploadCreativesModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        campaign={campaign}
        orchestrationStatus={orchestrationStatus}
        campaignMessages={campaignMessages}
        accountId={accountId}
        user={user}
        onUploadComplete={handleUploadComplete}
        accountSettings={accountSettings}
      />

      {/* Edit Campaign Modal */}
      <EditCampaignModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCampaignId(null);
        }}
        campaign={campaign}
        orchestrationStatus={orchestrationStatus}
        campaignMessages={campaignMessages}
        platformCampaignId={editingCampaignId}
        allPlatformCampaignIds={
          (getPlatformUrls() || [])
            .flatMap(u => u.campaigns ? u.campaigns.map(c => ({ id: c.campaign_id, name: c.name })) : u.campaign_id ? [{ id: u.campaign_id, name: '' }] : [])
            .filter(c => c.id)
        }
        onSubmit={handleEditCampaign}
        isSubmitting={isEditingCampaign}
        accountId={accountId}
        userId={user?.userId || user?.id}
      />

      {/* Optimize Campaign Modal */}
      <OptimizeCampaignModal
        analysisKind={optimizingAnalysisKind}
        isOpen={showOptimizeModal}
        onClose={() => {
          setShowOptimizeModal(false);
          setOptimizingCampaignId(null);
        }}
        campaign={campaign}
        platformCampaignId={optimizingCampaignId}
        accountId={accountId}
        user={user}
        onOptimizeComplete={handleOptimizeComplete}
      />

      {/* Create Geofence Ad Groups Modal (TTD, feature-flagged) */}
      <GeofenceAdGroupsModal
        isOpen={showGeofenceModal}
        onClose={() => {
          setShowGeofenceModal(false);
          setGeofenceCampaignId(null);
        }}
        campaign={campaign}
        platformCampaignId={geofenceCampaignId}
        campaignOptions={getTtdCampaignOptions()}
        accountId={accountId}
        user={user}
        onComplete={performCreateGeofence}
      />

    </div>
  );
};




export default CampaignDetailPage;
